/**
 * ESCHER FALLS — 全モジュールが共有する型定義。
 *
 * このファイルが型の唯一の定義元である。他モジュールは型をここから import し、
 * 独自に再定義しないこと。
 *
 * 規約:
 * - **座標系**: 右手系・**+Y が上**（three.js 既定）。1 ワールド単位 = グリッド1マス。
 * - **時間**: このプロジェクトの時間はすべて **ミリ秒 (ms)** の `number` で表す。
 *   `update(dt)` 系の引数も例外なく ms。秒を渡さないこと。
 * - **角度**: ラジアン。
 * - **不変**: ここで定義する型はすべて `readonly`。生成後に書き換えてはならない。
 *   状態の更新は「新しいオブジェクトを作る」ことで行う。
 * - このファイルは three.js に依存しない（`core/` 全体の規約）。
 */

// =============================================================================
// 1. 基本の幾何型
// =============================================================================

/**
 * ワールド座標 `[x, y, z]`。単位はグリッドマス。**+y が上**。
 *
 * ピースの `pos` は必ず整数。ポート座標 {@link Port.pos} だけが `.5` 刻みになりうる
 * （かつ「.5 な成分はちょうど1個」— 詳細は {@link Port.pos} の注記を参照）。
 */
export type Vec3 = readonly [number, number, number];

/** 6方向の軸。`'+x'` は +X 方向を指す単位ベクトル `[1,0,0]` に対応する。 */
export type Axis6 = '+x' | '-x' | '+y' | '-y' | '+z' | '-z';

/**
 * 方位。`k → k+1` はカメラ方向を Y 軸まわりに **+90°**（`(x,z) → (z,-x)`）回した向き。
 * 水平方向は `(1,·,1) → (1,·,-1) → (-1,·,-1) → (-1,·,1)`。巡回する。
 */
export type Azimuth = 0 | 1 | 2 | 3;

/**
 * 仰角。`0` = 低 (A, 35.264°)、`1` = 高 (B, 54.736°)、`2` = 真上 (T, 90°)。
 * 端（0 と 2）で止まる。巡回しない。
 */
export type Elevation = 0 | 1 | 2;

/**
 * カメラ姿勢。**12 種類**（方位4 × 仰角3）。VIEW-EXPANSION-SPEC §2.2。
 *
 * 「どこから見ているか」を表す。**「何がつながるか」は {@link ViewRelation} が表す。**
 * 真上 (T) の4姿勢は絵が回るだけで接続関係は同一なので、この2つは1対1ではない。
 */
export interface ViewPose {
  readonly azimuth: Azimuth;
  readonly elevation: Elevation;
}

/**
 * 接続関係。**9 種類。** `A0..A3 = 0..3`、`B0..B3 = 4..7`、`T = 8`。
 *
 * 「その視点で何と何が重なって見えるか」を決める同値類。
 * 2点は差ベクトルが下表の方向ベクトルの**実数倍**のとき、かつそのときに限り重なる。
 *
 * | 関係 | 方向ベクトル |
 * |---|---|
 * | 0..3 (A) | `(±1, 1, ±1)` |
 * | 4..7 (B) | `(±1, 2, ±1)` |
 * | 8 (T) | `(0, 1, 0)` — x と z が一致する縦の並び |
 *
 * **回転・仰角の補間中の中間状態をこの型で表してはならない。**
 * 接続判定に渡すのは必ず停止時の離散値である。補間中は数値的に偽の接続が成立し、
 * 仰角遷移では `|d| = 2.12` の完全一致ゴーストが 5〜28ms 滞在する
 * （PROJECTION-9VIEW §3.8。閾値を下げても消えない）。
 */
export type ViewRelation = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/**
 * 等角投影後の画面座標。**ワールド単位**（ピクセルでも NDC でもない）。
 * `x` は右が正、`y` は上が正。CORE-SPEC §1.3 の `sx`, `sy`。
 *
 * 接続判定はこの単位で行う。NDC で判定するとアスペクト補正で
 * `CONNECT_EPS` の意味が縦横で変わってしまう（PROJECTION-VERIFIED §4.3）。
 */
export interface ScreenPos {
  readonly x: number;
  readonly y: number;
}

/**
 * 軸平行境界ボックス（ワールド単位）。カメラの frustum 算出に使う。
 * `center` は `(min + max) / 2`（呼び出し側で再計算しなくて済むように含める）。
 * 不変条件: 各成分について `min[i] <= max[i]`。
 */
export interface Bounds {
  readonly min: Vec3;
  readonly max: Vec3;
  readonly center: Vec3;
}

// =============================================================================
// 2. 識別子
// =============================================================================

/** ピースID。レベル内で一意。JSON で `id` 未指定なら `"p0"`, `"p1"`, … が自動採番される。 */
export type PieceId = string;

/** ポートID。`` `${pieceId}:${portName}` `` の形式。レベル内で一意。 */
export type PortId = string;

/** セグメントID。`` `${pieceId}:seg` `` の形式。レベル内で一意。 */
export type SegmentId = string;

/** 錯視接続（リンク）ID。`` `${portIdA}|${portIdB}` ``（`portIdA < portIdB` の辞書順）。 */
export type LinkId = string;

/**
 * ピース内でのポートの呼び名。{@link PortId} の後半部分になる。
 * どのピース種別がどの名前を持つかは MODULE-CONTRACTS.md `core/pieces.ts` に完全に規定。
 *
 * - `'out'` … source の唯一のポート
 * - `'in'` … goal の唯一のポート
 * - `'a'` / `'b'` … straight / corner / waterwheel の2ポート
 * - `'top'` / `'bottom'` … fall の2ポート
 */
export type PortName = 'out' | 'in' | 'a' | 'b' | 'top' | 'bottom';

// =============================================================================
// 3. レベルJSON上の生のピース定義（判別可能ユニオン）— CORE-SPEC §4.1
// =============================================================================

/** ピース種別。 */
export type PieceType =
  | 'source'
  | 'straight'
  | 'corner'
  | 'fall'
  | 'waterwheel'
  | 'goal'
  | 'drain'
  | 'pillar'
  | 'lever'
  | 'turntable';

/** 全 PieceDef 共通のフィールド。 */
interface PieceDefBase {
  /**
   * 明示ID（省略可）。省略時は配列インデックスから `"p{index}"` が採番される。
   * 明示する場合はレベル内で一意でなければならない。
   */
  readonly id?: string;
  /**
   * 占有セルの**基準セル**の中心座標。**必ず整数**でなければならない
   * （PROJECTION-VERIFIED §2.4：非整数の `pos` はポート座標を壊し誤接続を生む）。
   * どのセルが基準になるかは種別ごとに MODULE-CONTRACTS.md で規定。
   */
  readonly pos: Vec3;
  /**
   * レバーが入っているときの変位（省略＝動かない）。**必ず整数**。
   *
   * 同じ空間から別の発見を出すための仕掛け（第二章）。
   * 切り替えは離散的なので、「接続グラフの再構築は停止した瞬間のみ」という
   * 規則を壊さない——補間の途中という状態が存在しないため。
   */
  readonly shift?: Vec3;
  /**
   * レバーが入っているときの**回転**（省略＝回らない）。`shift` と併用しない。
   *
   * **回る足場**の仕掛け。`about` を通る鉛直軸のまわりに、上から見て
   * `quarters` × 90° 回す。軸は整数のマス中心、角度は 90° 刻みに限るので、
   * 回した後も `pos` は整数のまま、ポートの「.5 の成分はちょうど1個」も保たれる。
   * 滑らせる（`shift`）のと違い、**水路の向きそのものが変わる**——
   * 同じ場所にある水路が、倒すと別の向きの継ぎ目になる。
   */
  readonly turn?: Turn;
}

/** 回る足場の回転。{@link PieceDefBase.turn}。 */
export interface Turn {
  /** 回転軸が通るマスの `[x, z]`。整数。 */
  readonly about: readonly [number, number];
  /** 上から見て反時計回りに何回 90° 回すか（1〜3）。 */
  readonly quarters: 1 | 2 | 3;
}

/**
 * 回る足場の台（装飾）。ポートもセグメントも持たず、1マスを占める。
 * 水路の真下に置き、**ここが回る**ことを見せる。自分も `turn` を持ち、
 * レバーを倒すと上面の刻みが回った向きを指す。
 */
export interface TurntableDef extends PieceDefBase {
  readonly type: 'turntable';
  /** 台の半径（マス）。1 以上の整数。上に載る水路を覆う大きさにする。 */
  readonly radius: number;
}

/** レバー。ポートもセグメントも持たない。触ると `shift` を持つピースが動く。 */
export interface LeverDef extends PieceDefBase {
  readonly type: 'lever';
}

/** 水源。ポートは `pos + dir·0.5` の1つだけ。セグメントは持たない。 */
export interface SourceDef extends PieceDefBase {
  readonly type: 'source';
  /** 開口部が向いている**外向き**の方向。水はこの向きに出ていく。 */
  readonly dir: Axis6;
}

/** 直線水路。`pos` から `dir` 方向へ `length` マス伸びる。 */
export interface StraightDef extends PieceDefBase {
  readonly type: 'straight';
  /** パイプが伸びる方向。`'+y'`/`'-y'` も可（垂直な閉じたパイプ）。 */
  readonly dir: Axis6;
  /** マス数。1 以上の整数。 */
  readonly length: number;
}

/** L字。1マスを占有し、`from` 面と `to` 面に開口を持つ。 */
export interface CornerDef extends PieceDefBase {
  readonly type: 'corner';
  /** 開口Aが向いている外向き方向。 */
  readonly from: Axis6;
  /** 開口Bが向いている外向き方向。`from` とは**異なる軸**でなければならない。 */
  readonly to: Axis6;
}

/** 垂直落下。`pos` を最上段として `height` マス**下**へ伸びる。 */
export interface FallDef extends PieceDefBase {
  readonly type: 'fall';
  /** マス数。1 以上の整数。 */
  readonly height: number;
}

/** 水車。1マスを占有し、接続としては長さ1の直線と等価。 */
export interface WaterwheelDef extends PieceDefBase {
  readonly type: 'waterwheel';
  /**
   * 通水軸。省略時は `'+y'`（上から下へ水が抜ける縦置き）。
   * ポートは `pos ± dir·0.5` の2つなので、`dir` とその逆向きは同じポート対を与える
   * （`dir` はメッシュの向き付けと `a`/`b` の割り当てだけを決める）。
   */
  readonly dir?: Axis6;
}

/** ゴール（水受け）。ポートは `pos + dir·0.5` の1つだけ。セグメントは持たない。 */
export interface GoalDef extends PieceDefBase {
  readonly type: 'goal';
  /** 開口部が向いている**外向き**の方向。水はこの向きから入ってくる。 */
  readonly dir: Axis6;
}

/**
 * 涸れ井戸。**水が入ってはいけない受け口。**
 *
 * これまでの仕掛けはすべて「繋げる」方向だったが、これは初めて
 * **「繋げてはいけない」**を要求する。水源からゴールへ届き、かつここへは
 * 届かない視点を探すことになり、視点を*除外*する読みが要る。
 *
 * 形はゴールと同じ（ポートは1つ）。違うのは意味だけで、
 * ここに水が届く視点は**解ではない**（`isSolvable` が弾く）。
 */
export interface DrainDef extends PieceDefBase {
  readonly type: 'drain';
  /** 開口部が向いている**外向き**の方向。 */
  readonly dir: Axis6;
}

/** 装飾の柱。`pos` を最下段として `height` マス**上**へ伸びる。ポート・セグメントなし。 */
export interface PillarDef extends PieceDefBase {
  readonly type: 'pillar';
  /** マス数。1 以上の整数。 */
  readonly height: number;
}

/**
 * レベルJSONに書かれる生のピース定義。`type` で判別する。
 * 展開規則（占有セルとポート座標）は MODULE-CONTRACTS.md `core/pieces.ts` に完全に規定。
 */
export type PieceDef =
  | SourceDef
  | StraightDef
  | CornerDef
  | FallDef
  | WaterwheelDef
  | GoalDef
  | DrainDef
  | PillarDef
  | LeverDef
  | TurntableDef;

// =============================================================================
// 4. 展開後のピース実体
// =============================================================================

/**
 * パイプの開口部。接続グラフのノード。
 */
export interface Port {
  readonly id: PortId;
  readonly pieceId: PieceId;
  /** このピース内での呼び名。 */
  readonly name: PortName;
  /**
   * 開口部の**中心**のワールド座標。
   *
   * - **`.5` 刻みになりうる。** 整数セルの面の中心に来るため。
   * - **不変条件（破ると誤接続する）**: 3成分のうち `.5` な成分は**ちょうど1個**。
   *   `pos` を整数に保ち、オフセットを単一軸方向の `±(n - 0.5)` に限れば自動的に満たされる。
   *   PROJECTION-VERIFIED §2.4 参照：`(0.5, 0, 0)` と `(0, 0.5, 0.5)` は差が
   *   `(-1,1,1)` の -0.5 倍になり **A3 で誤って接続してしまう**。
   *   「高々1個」に緩めると誤接続の下限が `√(1/12) = 0.2887` まで落ちる
   *   （PROJECTION-9VIEW §3.6/§3.7）。**ちょうど1個**であることが
   *   真上 (T) の半整数倍接続を不可能にしており、そこに依存している。
   * - y は上向き。
   */
  readonly pos: Vec3;
  /**
   * 開口部が向いている**外向き**の方向（ピース中心から見て外へ出る向き）。
   * 描画（口の向き）・デバッグ表示・行き止まりのこぼれ演出に使う。
   * 接続判定には使わない（判定は {@link pos} だけで決まる）。
   */
  readonly dir: Axis6;
}

/**
 * ピース内部で水が通る1本の区間。接続グラフの辺のうち「ピース内」のもの。
 *
 * `fromPort` → `toPort` は**名目上の向きにすぎない**。接続グラフは無向であり、
 * 水は逆向きにも流れる（{@link FlowStep.reversed} を参照）。
 */
export interface Segment {
  readonly id: SegmentId;
  readonly pieceId: PieceId;
  readonly fromPort: PortId;
  readonly toPort: PortId;
  /**
   * この区間の長さ（マス数）。1 以上の整数。
   * 充填時間 = `cells * FILL_MS_PER_CELL`、`waterBudget` の消費量 = `cells`。
   */
  readonly cells: number;
}

/**
 * {@link PieceDef} から展開された実体。
 */
export interface Piece {
  readonly id: PieceId;
  /** 元の定義（描画で `length` / `height` / `dir` などを参照するため保持する）。 */
  readonly def: PieceDef;
  /** このピースが持つポート。0〜2個。順序は決定的（MODULE-CONTRACTS.md で規定）。 */
  readonly ports: readonly Port[];
  /** このピースが持つセグメント。0 個または 1 個。 */
  readonly segments: readonly Segment[];
  /**
   * 占有する整数セルの中心座標の一覧。**すべて整数**。
   * レベルの bbox 算出とピース重なり検証に使う。`pillar` も含め全種別が 1 個以上持つ。
   */
  readonly cells: readonly Vec3[];
}

// =============================================================================
// 5. レベル
// =============================================================================

/**
 * ディスク上の `src/levels/*.json` の形。**純粋な JSON**（コメント不可）。
 *
 * 実際の読み込みは `game/levelLoader.ts` の `parseLevel(raw: unknown): Level` が
 * `unknown` から検証しつつ行う。この型は「書き手のための形の説明」であり、
 * JSON import の推論結果がそのまま代入できるとは限らない。
 */
export interface LevelJson {
  readonly id: string;
  readonly name: string;
  /** 旧形式の数値（＝方位、仰角 0）も、`{azimuth, elevation}` も受け付ける。 */
  readonly startView: number | { readonly azimuth: number; readonly elevation: number };
  /** 省略可。省略＝無制限。 */
  readonly waterBudget?: number;
  /** 省略可。省略＝制限なし。 */
  readonly timeLimitMs?: number;
  /** 省略可。省略＝無制限。方位回転と仰角移動の合計の上限。 */
  readonly maxMoves?: number;
  /** 省略可。 */
  readonly hint?: string;
  readonly pieces: readonly PieceDef[];
}

/**
 * 検証・正規化済みのレベル。`game/levelLoader.ts` の出力。
 * 省略可能フィールドは `null` に正規化されている（`undefined` は現れない）。
 */
export interface Level {
  /** レベル識別子。ファイル名と一致させる。 */
  readonly id: string;
  /** 日本語のステージ名。HUD に表示する。 */
  readonly name: string;
  /**
   * 開始姿勢。**解の関係であってはならない**（`tools/validate-levels.ts` が検証）。
   * レベル JSON では数値（旧形式）でも書けるが、ここには正規化済みの姿勢が入る。
   */
  readonly startView: ViewPose;
  /** 満たせるパイプ総マス数の上限。`null` なら無制限。指定時は 1 以上の整数。 */
  readonly waterBudget: number | null;
  /**
   * 制限時間 (ms)。`null` なら制限なし。
   * **本編には設定しない**（METAGAME-SPEC §1.3）。じっくり観察する遊びを壊さないため。
   * エクストラと二周目にのみ設定する。
   */
  readonly timeLimitMs: number | null;
  /**
   * 使える手数（方位回転＋仰角移動の合計）の上限。`null` なら無制限。
   * 超えた瞬間に `failed`。**二周目専用**（METAGAME-SPEC §5）。
   * 「見てから回す」思考を強制するためのもので、初見の面に付けると理不尽になる。
   */
  readonly maxMoves: number | null;
  /** ヒント文（日本語）。`null` ならヒントなし。 */
  readonly hint: string | null;
  readonly pieces: readonly PieceDef[];
}

// =============================================================================
// 6. 接続グラフ
// =============================================================================

/**
 * 錯視接続。異なるピースに属する2つのポートが、ある view で画面上重なっている状態。
 * リンクを渡るのに時間はかからない（継ぎ目であってパイプではない）。
 *
 * 不変条件: `a < b`（`PortId` の辞書順）。`a` と `b` は必ず異なるピースに属する。
 */
export interface Link {
  readonly id: LinkId;
  readonly a: PortId;
  readonly b: PortId;
}

/**
 * 接続グラフの辺。ポートから見た「隣」への1手。
 * - `'segment'` … 同一ピース内のパイプを通る。時間とマス数を消費する。
 * - `'link'` … 錯視接続を渡る。時間もマス数も消費しない。
 */
export type GraphEdge =
  | { readonly kind: 'segment'; readonly segmentId: SegmentId; readonly to: PortId }
  | { readonly kind: 'link'; readonly linkId: LinkId; readonly to: PortId };

/**
 * ある view における接続グラフ。ノード = {@link Port}、辺 = {@link Segment}（ピース内）
 * ＋ {@link Link}（錯視接続）。**無向**。
 *
 * **このオブジェクトを回転補間の途中で作ってはならない。**
 * `rotating → idle` に遷移した瞬間にのみ再構築する（CORE-SPEC §3.1 / PROJECTION-VERIFIED §6）。
 */
export interface ConnectionGraph {
  /** このグラフを構築した接続関係。**姿勢ではない。** */
  readonly relation: ViewRelation;
  /** 全ポート。キーは `Port.id`。 */
  readonly ports: ReadonlyMap<PortId, Port>;
  /** 全セグメント。キーは `Segment.id`。 */
  readonly segments: ReadonlyMap<SegmentId, Segment>;
  /** 成立している錯視接続。決定的な順序でソート済み（`Link.id` の辞書順）。 */
  readonly links: readonly Link[];
  /**
   * 隣接リスト。キーは `Port.id`。**すべてのポートがキーとして存在する**
   * （孤立ポートは空配列）。各配列も決定的な順序（segment が先、その後 link を id 順）。
   */
  readonly adjacency: ReadonlyMap<PortId, readonly GraphEdge[]>;
}

// =============================================================================
// 7. 放水プラン
// =============================================================================

/** 1つのセグメントを充填するステップ。 */
export interface FlowStep {
  readonly segmentId: SegmentId;
  /** 放水開始からの経過時間 (ms)。このセグメントが満ち始める時刻。 */
  readonly startTime: number;
  /** 充填にかかる時間 (ms)。`cells * FILL_MS_PER_CELL`。 */
  readonly duration: number;
  /** 水が入ってくる側のポート。 */
  readonly fromPort: PortId;
  /** 水が出ていく側のポート。 */
  readonly toPort: PortId;
  /**
   * `Segment.fromPort → Segment.toPort` に対して逆向きに流れるか。
   * `render/water.ts` はこれを見て充填アニメの向きを決める。
   */
  readonly reversed: boolean;
}

/** 行き止まりのポートから水がこぼれるイベント。 */
export interface SpillEvent {
  readonly portId: PortId;
  /** 放水開始からの経過時間 (ms)。こぼれ始める時刻。 */
  readonly time: number;
}

/**
 * 放水時に再生する充填順序。`core/graph.ts` の `planFlow()` が作る純データ。
 * 時間はすべて「放水開始を 0 とした ms」。
 */
export interface FlowPlan {
  /** 充填ステップ。`startTime` の昇順にソート済み。 */
  readonly steps: readonly FlowStep[];
  /** ゴールポートに水が到達するか。 */
  readonly reachesGoal: boolean;
  /** ゴール到達時刻 (ms)。`reachesGoal` が false なら `null`。 */
  readonly goalTime: number | null;
  /** 充填されるマス数の合計（= `waterBudget` の消費量）。 */
  readonly totalCells: number;
  /**
   * 演出全体の長さ (ms)。全ステップの終了時刻 `startTime + duration` と、
   * 全こぼれの `time + SPILL_DURATION_MS` の最大値。何も起きなければ 0。
   */
  readonly durationMs: number;
  /** 行き止まりでのこぼれ。`time` の昇順にソート済み。 */
  readonly spills: readonly SpillEvent[];
  /** `waterBudget` が尽きて途中で打ち切られたか。 */
  readonly budgetExceeded: boolean;
  /**
   * **涸れ井戸に水が入ったか。** 入ったら、ゴールに届いていても失敗。
   * 「繋げてはいけない」という初めての負の条件を担う。
   */
  readonly drained: boolean;
}

// =============================================================================
// 8. ゲーム状態
// =============================================================================

/**
 * ゲームの状態。CORE-SPEC §3.1。
 *
 * ```
 * idle ──(回転入力)──> rotating ──(補間完了)──> idle
 * idle ──(放水)──> flowing
 * flowing ──(ゴール到達)──> clear
 * flowing ──(水量切れ / 行き止まりで終了)──> failed
 * clear|failed ──(リトライ)──> idle
 * ```
 *
 * **放水は水路がつながっていなくても常に行える。**
 * つながっていなければ水は継ぎ目の切れ目から落ちて `failed` になる。
 * 「押せるかどうか」で解を教えてしまうと、盤面を見ずに総当たりで解けてしまい、
 * パズルとして成立しない。失敗は罰ではなく、どこで切れているかを見せる手段である。
 */
export type GamePhase = 'idle' | 'rotating' | 'flowing' | 'clear' | 'failed';

/**
 * ゲーム全体の状態のスナップショット。**イミュータブル**。
 * `game/state.ts` が更新のたびに新しいオブジェクトを作り、購読者に渡す。
 */
export interface GameState {
  readonly level: Level;
  /**
   * 展開済みピース。**レバーを倒すと組み直される**（`shift` を持つピースが動く）。
   * それ以外では変化しない。
   */
  readonly pieces: readonly Piece[];
  /** レバーが入っているか。レバーの無い面では常に false。 */
  readonly leverOn: boolean;
  /** この面にレバーがあるか。UI がボタンの出し分けに使う。 */
  readonly hasLever: boolean;
  readonly phase: GamePhase;
  /**
   * 現在**確定している**カメラ姿勢。遷移補間中は「遷移前の値」のままで、
   * 補間が完了した瞬間に新しい値になる。HUD の視点インジケータはこれを見る。
   */
  readonly pose: ViewPose;
  /**
   * `pose` に対応する接続関係。真上の4姿勢はすべて `8` になる。
   * **何がつながるかを決めるのはこちら。**
   */
  readonly relation: ViewRelation;
  /**
   * `relation` に対応する接続グラフ。`rotating → idle` の瞬間にのみ再構築される。
   * 補間中も「遷移前のグラフ」が入っており、途中の偽接続は決して現れない。
   */
  readonly graph: ConnectionGraph;
  /**
   * 放水ボタンを押せるか。**接続の成否とは無関係**で、水源とゴールが
   * 存在する限り `idle` の間は常に true。
   * ここに到達可能性を混ぜてはならない（ボタンが解答オラクルになる）。
   */
  readonly canPour: boolean;
  /**
   * 現在の view で水源→ゴールが到達可能か。
   * **UI に出してはならない。** デバッグ表示（`?debug=1`）と検証ツール専用。
   */
  readonly goalReachable: boolean;
  /** 放水中／終了後のプラン。`idle` では `null`。 */
  readonly flow: FlowPlan | null;
  /** 放水開始からの経過時間 (ms)。`flowing` 以外では 0。 */
  readonly flowTime: number;
  /** レベル開始からの回転回数（HUD の統計用）。 */
  readonly rotations: number;
  /**
   * 制限時間を使い切って `failed` に落ちたか。
   *
   * 失敗の理由を `flow` から逆算していると、これだけは区別できない
   * （時間切れは放水と無関係に起きるので `flow` が何であってもおかしくない）。
   * したがってこれだけは明示的に持つ。
   *
   * **時間切れからは「もういちど」では戻れない。** 水を引いても時間は戻らないため、
   * 復帰は「やりなおす」（面ごと読み直してタイマーもゼロに戻す）か、
   * 制限時間そのものを延ばすかのどちらかになる。
   */
  readonly timedOut: boolean;
}

// =============================================================================
// 9. 共有チューニング定数
// =============================================================================
// 複数モジュールが同じ値を必要とするものだけをここに置く。
// モジュール固有の定数（CONNECT_EPS, パレット等）は各モジュール側で定義する。

/**
 * 視点回転アニメーションの長さ (ms)。CORE-SPEC §5。
 * `render/cameraRig.ts`（補間）と `game/state.ts`（状態遷移）が共有する。
 */
export const ROTATE_DURATION_MS = 450;

/**
 * パイプ1マスを満たすのにかかる時間 (ms)。CORE-SPEC §3.2（0.35 秒）。
 * `core/graph.ts`（プラン生成）と `render/water.ts`（再生）が共有する。
 */
export const FILL_MS_PER_CELL = 350;

/**
 * 行き止まりからのこぼれ演出の長さ (ms)。
 * `core/graph.ts`（プラン末尾の余韻）と `render/water.ts`（パーティクル寿命）が共有する。
 */
export const SPILL_DURATION_MS = 900;
